// Boot (TECH_SPEC §4.4). Three ways in, in order of cheapness:
//   1. window.__BOOT__ inlined by the share page — zero round trips
//   2. /r/:code without inlined boot data → GET /api/boot/:code
//   3. no link at all → GET /api/track/current + GET /api/opponent
// Entry type follows from which path we took plus the device's history.

import './ui/styles.css';

import { DUEL_MIN_SOURCE_DEATH } from '@beatme/sim';

import {
  ensureDeviceId,
  fetchBootByCode,
  fetchCurrentTrack,
  fetchOpponent,
  flushPendingRun,
  hasPlayedBefore,
} from './api';
import { installAnalyticsFlush, track as trackEvent } from './analytics';
import { exposeAudioDebug, readDebugOptions } from './debug';
import { fairEnabled } from './fair';
import { duelAvailable, duelEnabled, handoffEnabled, isHandoff } from './duel';
import { detectLocale, setLocale, t } from './i18n';
import { ladderEnabled } from './ladder';
import { peakEnabled } from './peak';
import { ADS_DISABLED, initAds } from './game/ads';
import { Session, type RunContext } from './game/session';
import type { AdsConfig, BootData, EntryType, Opponent } from './types';
import { el } from './ui/dom';

interface ResolvedBoot {
  data: BootData & { opponent: Opponent };
  entryType: EntryType;
  parentRunId?: string;
  fromLink: boolean;
  flagged: boolean;
}

const SHARE_PATH = /^\/r\/([A-Za-z0-9]{3,12})\/?$/;

function shareCodeFromPath(pathname: string): string | null {
  const m = SHARE_PATH.exec(pathname);
  return m && m[1] ? m[1] : null;
}

/** §4bis.31: the verb the link carried. `?h=1` mirrors §4bis.28's `?d=1`. */
function verbFromLocation(): 'challenge' | 'handoff' {
  return new URLSearchParams(location.search).get('h') === '1' ? 'handoff' : 'challenge';
}

async function resolveBoot(deviceId: string): Promise<ResolvedBoot> {
  const inline = window.__BOOT__;
  const code = shareCodeFromPath(location.pathname);

  if (inline?.track) {
    // The share page already resolved the verb server-side and inlined the
    // result, so there is nothing to re-read here.
    return fromLinkBoot(inline, deviceId);
  }
  if (code) {
    return fromLinkBoot(await fetchBootByCode(code, verbFromLocation()), deviceId);
  }

  const played = hasPlayedBefore();
  const entryType: EntryType = played ? 'walkin' : 'first';
  const track = await fetchCurrentTrack();
  const opponent = await fetchOpponent(track.id, played ? 'walkin' : 'first');
  if (!opponent) throw new Error('no opponent available');
  return {
    data: { track, opponent },
    entryType,
    fromLink: false,
    flagged: opponent.flagged === true,
  };
}

/** §4.4: a link to your own run is a rerun, anyone else's is a link entry. */
async function fromLinkBoot(data: BootData, deviceId: string): Promise<ResolvedBoot> {
  // Flagged links can arrive without a ghost — substitute a default opponent.
  const opponent = data.opponent ?? (await fetchOpponent(data.track.id, 'walkin'));
  if (!opponent) throw new Error('no opponent available');
  const ownRun = data.run?.playerId !== undefined && data.run.playerId === deviceId;
  const entryType: EntryType = ownRun ? 'rerun' : 'link';
  const parentRunId = entryType === 'link' ? (data.run?.runId ?? data.run?.id) : undefined;
  return {
    data: { ...data, opponent },
    entryType,
    ...(parentRunId ? { parentRunId } : {}),
    fromLink: true,
    flagged: data.flaggedRun === true || data.flagged === true || data.opponent?.flagged === true,
  };
}

function showFatal(stage: HTMLElement, overlay: HTMLElement): void {
  overlay.append(
    el('div', { class: 'screen screen--intro' }, [
      el('div', { class: 'intro-line', text: t('app.error') }),
      el('button', {
        class: 'btn btn--primary',
        text: t('app.retryBoot'),
        attrs: { type: 'button' },
        on: { click: () => location.reload() },
      }),
    ]),
  );
  stage.querySelector('#boot-splash')?.remove();
}

async function main(): Promise<void> {
  setLocale(detectLocale());
  installAnalyticsFlush();
  // Offline SFX render hook (`npm run visual:audio`). Installed before anything can
  // fail, and not URL-gated: it needs no track, no boot data and no run, so
  // `tools/visual/src/audio.ts` just opens the page and calls it.
  if (__BEATME_DEBUG__) exposeAudioDebug();

  const stage = document.getElementById('stage');
  const canvas = document.getElementById('game');
  const overlay = document.getElementById('overlay');
  const splash = document.getElementById('boot-splash');
  if (!(stage instanceof HTMLElement) || !(canvas instanceof HTMLCanvasElement) || !overlay) {
    throw new Error('app shell missing');
  }
  if (splash) splash.textContent = t('app.loading');
  document.title = t('app.title');

  const deviceId = ensureDeviceId();

  let boot: ResolvedBoot;
  try {
    boot = await resolveBoot(deviceId);
  } catch {
    showFatal(stage, overlay);
    return;
  }
  splash?.remove();

  // §4.4: link_open fires only for share-link boots, after the JS has loaded.
  if (boot.fromLink) {
    // Seed posts carry ?c=<channel> (design §10.4: 每个渠道单独链接参数) and
    // experiment links carry ?v=<variant> (share-card A/B). Organic reshares
    // carry neither: whole-chain attribution recovers the channel server-side
    // from the chain root's tagged opens (TECH_SPEC §4bis.16).
    const params = new URLSearchParams(location.search);
    const channel = params.get('c');
    const variant = params.get('v');
    // §4bis.28's free dormant coverage read: could this link have minted a
    // slice? Derived from the linked run's death frame ALONE — a field already
    // on the boot payload, no slice computation — so it costs nothing, holds
    // dormancy, and tells us the mint rate while the flag is still 0.
    const deathFrame = boot.data.run?.deathFrame;
    const duelEligible =
      typeof deathFrame === 'number' && deathFrame >= DUEL_MIN_SOURCE_DEATH ? 1 : 0;
    trackEvent('link_open', {
      entryType: boot.entryType,
      shareCode: shareCodeFromPath(location.pathname),
      duelEligible,
      ...(channel ? { channel: channel.slice(0, 24) } : {}),
      ...(variant ? { variant: variant.slice(0, 24) } : {}),
    });
  }

  // Any run cached while offline (design §4.5) goes out now.
  void flushPendingRun();

  // Layer-2 playtest params (docs/DEV_PLAYTEST.md). `readDebugOptions` is a no-op
  // — and this whole branch is dead code rollup removes — in a normal build.
  const debug = __BEATME_DEBUG__ ? readDebugOptions(location.search) : null;
  const track =
    __BEATME_DEBUG__ && debug?.theme
      ? { ...boot.data.track, theme: debug.theme }
      : boot.data.track;

  // The official ghost IS the mascot (design §3.2), and the mascot's name is
  // ours to localize — unlike player nicknames, which are never translated.
  // The server stores the canonical 小跑; each viewer sees their language's name.
  const opponent = boot.data.opponent.official
    ? { ...boot.data.opponent, nickname: t('mascot.name') }
    : boot.data.opponent;

  // Design §8.2: the ad SDK is server-flagged and lazy — `initAds` only records
  // the answer; nothing is fetched until the first game_end, and nothing at all
  // while the flag is off. Autoplay never gets ads: a bot must not be offered a
  // revive, and every visual baseline has to stay pixel-identical.
  const adsCfg: AdsConfig =
    __BEATME_DEBUG__ && debug?.autoplay != null
      ? ADS_DISABLED
      : (boot.data.ads ?? boot.data.track.ads ?? ADS_DISABLED);
  initAds(adsCfg);

  // §4bis.21: the rival ladder is a server-side percentage (`LADDER_PCT`,
  // default 0) hashed against this device id — same both entry paths, like ads.
  // At 0 this is `false` for everybody and the ladder costs one comparison:
  // nothing is fetched, nothing is rendered. `&ladder=1` forces it on in debug
  // builds only (the visual harness's flag-forced shot, and phone testing).
  const ladderPct = boot.data.ladderPct ?? boot.data.track.ladderPct ?? 0;
  const ladder =
    (__BEATME_DEBUG__ && debug?.ladder === true) || ladderEnabled(deviceId, ladderPct);

  // §4bis.22: peak-moment share, the same dark-flag shape (`PEAK_PCT`, salt
  // `peak-v1` — independent of both other assignments by construction). At 0
  // this is `false` for everybody: no overlay, no amplification, and rendering
  // is pixel-identical to today. `&peak=1` forces it in debug builds only.
  const peakPct = boot.data.peakPct ?? boot.data.track.peakPct ?? 0;
  const peak = (__BEATME_DEBUG__ && debug?.peak === true) || peakEnabled(deviceId, peakPct);

  // §4bis.23: honest silhouettes, the same dark-flag shape (`FAIR_PCT`, salt
  // `fair-v1` — independent of the other three assignments by construction).
  // At 0 this is `false` for everybody and rendering is pixel-identical to
  // today. `&fair=1` forces it in debug builds only. Computed ONCE, here: a
  // run must never change geometry mid-flight.
  const fairPct = boot.data.fairPct ?? boot.data.track.fairPct ?? 0;
  const fair = (__BEATME_DEBUG__ && debug?.fair === true) || fairEnabled(deviceId, fairPct);

  // §4bis.28: the blitz duel, the same dark-flag shape (`DUEL_PCT`, salt
  // `duel-v1` — independent of the other four by construction). At 0 the server
  // sends no `duel` block at all, so `duelAvailable` is false for everybody and
  // the link opens as the full run it always did. `&duel=1` forces the arm in
  // debug builds only; the SLICE still has to exist, because only the server
  // can derive one.
  const duelPct = boot.data.duelPct ?? boot.data.track.duelPct ?? 0;
  const duelArmed = (__BEATME_DEBUG__ && debug?.duel === true) || duelEnabled(deviceId, duelPct);
  const slice = boot.data.duel;
  const duel = duelAvailable(boot.entryType, slice, duelArmed) ? slice : null;

  // §4bis.31: 帮我, the second verb, on its own dial (`HANDOFF_PCT`, salt
  // `handoff-v1` — the sixth independent partition). NESTED inside the duel
  // arm: a hand-back IS a duel, so there is no handoff-on/duel-off cell, and at
  // 0 this is false for everybody, no handoff DOM is built, and the death
  // screen is byte-for-byte what 005 shipped. `&handoff=1` forces the arm in
  // debug builds only; the server still has to have derived the slice, because
  // only it can.
  const handoffPct = boot.data.handoffPct ?? boot.data.track.handoffPct ?? 0;
  const handoffArm =
    duelArmed &&
    ((__BEATME_DEBUG__ && debug?.handoff === true) || handoffEnabled(deviceId, handoffPct));
  // The hand-back block, present iff the LINK carried `?h=1` and the server
  // minted a hand-back slice off it. A hand-back is identified solely by this.
  const handoff = duel && isHandoff(duel) ? (boot.data.handoff ?? {}) : null;
  // Is this device the person the life was addressed TO? Computed here, from
  // the payload, because a cold `/r/:code` navigation carries no device header
  // and a server-side answer would tell the addressed person they are a
  // stranger — which is exactly the read T1 exists to measure. The card is
  // addressed; the link stays open to anyone who opens it.
  const handoffAddressed = handoff?.toPlayerId !== undefined && handoff.toPlayerId === deviceId;

  const ctx: RunContext = {
    track,
    opponent,
    entryType: boot.entryType,
    ...(boot.parentRunId ? { parentRunId: boot.parentRunId } : {}),
    flagged: boot.flagged,
    firstEver: !hasPlayedBefore(),
    ladder,
    peak,
    fair,
    duel,
    duelArm: duelArmed,
    handoffArm,
    handoff,
    handoffAddressed,
    // §4bis.29: carried through untouched from whichever boot path served us —
    // inlined `__BOOT__` on /r/:code, or the /api/boot/:code fetch. It is the
    // server's own note that it delivered this run to this device, and it is
    // what lets a duel attach to the chain. Set from the boot payload rather
    // than from `duel`, so it is present whenever the server issued one; only
    // duel submits actually send it (session.ts), because attachment is the
    // only thing it decides.
    ...(boot.data.delivery?.token ? { deliveryToken: boot.data.delivery.token } : {}),
  };

  const session = new Session({ canvas, overlay, stage }, ctx, debug);
  await session.start();
}

void main();
